import {
  encryptChunk as encryptChunkMain,
  decryptChunk as decryptChunkMain,
} from './crypto.js';

// Die tatsächlich ausgelieferte Adresse des Krypto-Workers. Der Maschinenraum verlinkt
// darauf, damit niemand raten muss, welche Datei im Browser läuft. Der Build schreibt
// hier den fertigen Dateinamen mit Inhalts-Prüfsumme hinein.
export const CRYPTO_WORKER_URL = new URL('./cryptoWorker.js', import.meta.url).href;

// Mehrere Worker, damit Ver- und Entschluesselung parallel laufen, wenn mehrere Bloecke gleichzeitig
// unterwegs sind (bis zu 6 beim Hochladen, 4 beim Herunterladen). Auf starken Rechnern ist AES-GCM
// selten der Engpass, auf schwachen Handys schon. Jeder Worker bekommt nur Block und Dateischluessel.
const POOL_SIZE = Math.max(1, Math.min(4, (navigator.hardwareConcurrency || 2) - 1));
const workers = [];
let workerFailed = false;

/** Läuft die Verschlüsselung gerade im Worker oder im Hauptstrang? */
export function isWorkerActive() {
  return !workerFailed && workers.length > 0;
}
/** Wie viele Krypto-Worker parallel arbeiten (für den Maschinenraum). */
export function workerPoolSize() {
  return workerFailed ? 0 : POOL_SIZE;
}
let nextId = 0;

function failAll() {
  workerFailed = true;
  for (const w of workers.splice(0)) {
    for (const [, entry] of w.pending) entry.reject(new Error('crypto_worker_crashed'));
    w.pending.clear();
    try { w.worker.terminate(); } catch (_) { /* schon weg */ }
  }
}

function spawn() {
  const worker = new Worker(new URL('./cryptoWorker.js', import.meta.url), { type: 'module' });
  const slot = { worker, pending: new Map() };
  worker.onmessage = (event) => {
    const { id, ok, result, error } = event.data;
    const entry = slot.pending.get(id);
    if (!entry) return;
    slot.pending.delete(id);
    if (ok) entry.resolve(result);
    else entry.reject(new Error(error || 'crypto_worker_failed'));
  };
  worker.onerror = () => failAll();
  return slot;
}

// Der Worker mit den wenigsten offenen Auftraegen; neue Worker erst, wenn alle beschaeftigt sind.
function getWorker() {
  if (workerFailed) return null;
  try {
    let best = null;
    for (const w of workers) if (!best || w.pending.size < best.pending.size) best = w;
    if ((!best || best.pending.size > 0) && workers.length < POOL_SIZE) {
      best = spawn();
      workers.push(best);
    }
    return best;
  } catch (_) {
    failAll();
    return null;
  }
}

function callWorker(op, payload, transfer = []) {
  const slot = getWorker();
  if (!slot) return null;
  const id = ++nextId;
  return new Promise((resolve, reject) => {
    slot.pending.set(id, { resolve, reject });
    slot.worker.postMessage({ id, op, payload }, transfer);
  });
}

export async function encryptChunk(arrayBuffer, key, fileId, chunkIndex) {
  try {
    const copy = arrayBuffer.slice(0);
    const viaWorker = await callWorker(
      'encryptChunk',
      { buffer: copy, key, fileId, chunkIndex },
      [copy]
    );
    if (viaWorker) return viaWorker;
  } catch (_) {
    // CryptoKey is not always cloneable into a Worker — main-thread fallback must work.
    failAll();
  }
  return encryptChunkMain(arrayBuffer, key, fileId, chunkIndex);
}

export async function decryptChunk(combinedArrayBuffer, key, fileId, chunkIndex) {
  const copy = combinedArrayBuffer.slice(0);
  const viaWorker = await callWorker(
    'decryptChunk',
    { buffer: copy, key, fileId, chunkIndex },
    [copy]
  );
  if (viaWorker) return viaWorker.buffer;
  return decryptChunkMain(combinedArrayBuffer, key, fileId, chunkIndex);
}

export { decryptBlobStream, decryptBlob } from './crypto.js';
